import { AfterViewInit, Component, ElementRef, Inject, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { VcsService } from '../../services/vcs.service';
import { isEqual } from 'lodash-es';
import { BaseComponent } from '@wms/app/components';
import { MergeService } from '@wms/app/workspace/merge-changes/merge.service';
import { IFileDiffResponse } from '../../interface/file-diff-response';
import { IConflictFileDiff } from '@wms/app/workspace/merge-changes/interface/conflicted-file-diff';
import { ScriptLoaderService } from '@wm/core';

declare const window: any
@Component({
  selector: 'wms-diff-editor',
  standalone: true,
  templateUrl: './diff-editor.component.html',
  styleUrls: ['./diff-editor.component.less'],
  imports: [CommonModule]
})
export class DiffEditorComponent extends BaseComponent implements AfterViewInit, OnChanges, OnDestroy {
  public vcsService: VcsService;
  public mergeService: MergeService
  @ViewChild("wmsDiffEditor") wmsDiffEditorEl!: ElementRef;
  @Input() diffFileContent: IFileDiffResponse
  @Input() conflictedFileDiff: IConflictFileDiff
  @Input() isTwoWay: boolean
  @Input() isDiffMode: boolean = false
  @Input() isRevertAllowed: boolean = true
  @Input() isReadOnly: boolean = false
  private scriptLoaderService = inject(ScriptLoaderService);
  baseURL: string = 'assets';
  appBaseMetaInfo: any;

  constructor(@Inject("APP_BUILD_META_INFO") appBaseMetaInfo: any) {
    super()
    this.appBaseMetaInfo = appBaseMetaInfo;
    this.baseURL = this.appBaseMetaInfo.CDN_BASE_URL ? this.appBaseMetaInfo.CDN_BASE_URL + this.baseURL : this.baseURL;
    this.vcsService = inject(VcsService);
    this.mergeService = inject(MergeService)
  }

  private configureRequire(skipConfig: boolean, callback: Function) {
    if (skipConfig) {
      callback()
      return
    }
    window.require([`${this.baseURL}/require.config.js`], (configFunction: any) => {
      configFunction(this.baseURL)
      callback()
    })
  }

  private loadCodemirror(callback: Function) {
    window.require(["codemirror", "codemirror/addon/merge/merge", "codemirror/addon/mode/loadmode", "codemirror/mode/meta"], function (codemirror: any) {
      callback(codemirror)
    })
  }

  private initDiffEditor(skipConfig: boolean) {
    this.configureRequire(skipConfig, () => {
      this.loadCodemirror((codemirror: any) => {
        this.createMergeViewEditor(codemirror)
      })
    })
  }

  private createMergeViewEditor(codemirror: any) {
    const options = {
      value: this.isTwoWay ? this.diffFileContent?.rightVCSFileData.fileContent : this.isDiffMode ? this.conflictedFileDiff.fileContentMapByLabel.HEAD : this.conflictedFileDiff.fileContentMapByLabel.COMMON_ANCESTOR,
      origLeft: this.isTwoWay ? this.diffFileContent?.leftVCSFileData.fileContent : this.conflictedFileDiff.fileContentMapByLabel.THEIRS,
      origRight: this.isTwoWay ? null : this.conflictedFileDiff.fileContentMapByLabel.OURS,
      revertButtons: this.isRevertAllowed,
      lineNumbers: true,
      highlightDifferences: true,
      connect: null,
      collapseIdentical: true,
      allowEditingOriginals: false,
      autoRefresh: true,
      readOnly: this.isReadOnly,
      ignoreWhitespace: false
    }
    this.vcsService.diffEditor = codemirror.MergeView(this.wmsDiffEditorEl.nativeElement, options);
    this.loadEditorMode(codemirror)
  }

  ngAfterViewInit(): void {
    if (this.scriptLoaderService.isRequireScriptLoaded(this.baseURL)) {
      this.initDiffEditor(true);
    } else {
      const wmsEditorLoaderScript: HTMLScriptElement = document.head.appendChild(this.scriptLoaderService.createLoaderScriptElement(this.baseURL))
      wmsEditorLoaderScript.onload = (): void => {
        this.initDiffEditor(false)
      }
    }

    $(document).on('mouseover.CodeMirorEvents', '.CodeMirror-merge-copy', (event) => {
      let currentEl = $(event.currentTarget)
      let parent = $(currentEl).parent()
      if (parent.hasClass('CodeMirror-merge-copybuttons-right')) {
        $(currentEl).attr('title', this.locale["LABEL_" + this.mergeService.mergeOperationType + "_APPLY_OURS"]);
      } else if (parent.hasClass('CodeMirror-merge-copybuttons-left')) {
        $(currentEl).attr('title', this.isTwoWay ? this.locale.LABEL_PULL_CHANGES_APPLY_THEIRS : this.locale["LABEL_" + this.mergeService.mergeOperationType + "_APPLY_THEIRS"]);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['diffFileContent'] && !changes['diffFileContent']?.isFirstChange() && !isEqual(changes['diffFileContent'].currentValue, changes['diffFileContent'].previousValue)) || (changes['conflictedFileDiff'] && !changes['conflictedFileDiff']?.isFirstChange() && !isEqual(changes['conflictedFileDiff'].currentValue, changes['conflictedFileDiff'].previousValue))) {
      const mergeEditorEl = this.wmsDiffEditorEl.nativeElement.getElementsByClassName('CodeMirror-merge')
      if (mergeEditorEl.length > 0) {
        this.wmsDiffEditorEl.nativeElement.removeChild(mergeEditorEl[0])
      }
      if (this.scriptLoaderService.isRequireScriptLoaded(this.baseURL)) {
        this.initDiffEditor(true)
      } else {
        const wmsEditorLoaderScript: HTMLScriptElement = document.head.appendChild(this.scriptLoaderService.createLoaderScriptElement(this.baseURL))
        wmsEditorLoaderScript.onload = (): void => {
          this.initDiffEditor(false)
        }
      }
    }
  }

  private loadEditorMode(codemirror: any) {
    const fileName = this.isTwoWay ? this.diffFileContent?.filePath : this.conflictedFileDiff.filePath
    var val = fileName, m, mode, spec;
    if (m = /.+\.([^.]+)$/.exec(val)) {
      var info = codemirror.findModeByExtension(m[1]);
      if (info) {
        mode = info.mode;
        spec = info.mime;
      }
    } else if (/\//.test(val)) {
      var info = codemirror.findModeByMIME(val);
      if (info) {
        mode = info.mode;
        spec = val;
      }
    } else {
      mode = spec = val;
    }

    this.vcsService.diffEditor.editor().setOption("mode", spec);
    codemirror.autoLoadMode(this.vcsService.diffEditor.editor(), mode, {
      path: (mode: string) => {
        return "codemirror/mode/" + mode + '/' + mode
      },
    });
    this.vcsService.diffEditor.leftOriginal().setOption("mode", spec);
    codemirror.autoLoadMode(this.vcsService.diffEditor.leftOriginal(), mode, {
      path: (mode: string) => {
        return "codemirror/mode/" + mode + '/' + mode
      },
    });

    if (!this.isTwoWay) {
      this.vcsService.diffEditor.rightOriginal().setOption("mode", spec);
      codemirror.autoLoadMode(this.vcsService.diffEditor.rightOriginal(), mode, {
        path: (mode: string) => {
          return "codemirror/mode/" + mode + '/' + mode
        },
      });
    }

  }

  override ngOnDestroy(): void {
    $(document).off('.CodeMirorEvents')
  }
}
